// frontend/src/components/Quality/MeasurementEntry.jsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { measurementAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import './MeasurementEntry.css';

// ─── Fraction utilities ───────────────────────────────────────────────────────
const STANDARD_FRACS = [
  { label: '1', value: 1 }, { label: '7/8', value: 7/8 }, { label: '3/4', value: 3/4 },
  { label: '5/8', value: 5/8 }, { label: '1/2', value: 1/2 }, { label: '3/8', value: 3/8 },
  { label: '1/4', value: 1/4 }, { label: '1/8', value: 1/8 }, { label: '1/16', value: 1/16 },
];

const fractionToDecimal = (str) => {
  if (str === null || str === undefined || str === '') return null;
  const s = String(str).trim();
  if (/^-?\d+\.?\d*$/.test(s)) return parseFloat(s);
  const mixed = s.match(/^(-?)(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) return (mixed[1] === '-' ? -1 : 1) * (parseInt(mixed[2]) + parseInt(mixed[3]) / parseInt(mixed[4]));
  const frac = s.match(/^(-?)(\d+)\/(\d+)$/);
  if (frac) return (frac[1] === '-' ? -1 : 1) * parseInt(frac[2]) / parseInt(frac[3]);
  return parseFloat(s) || null;
};

const decimalToFrac = (d) => {
  if (d === null || d === undefined) return '';
  const sign = d < 0 ? -1 : 1;
  const abs = Math.abs(d);
  const whole = Math.floor(abs);
  const frac = abs - whole;
  const best = STANDARD_FRACS.reduce((b, f) => Math.abs(f.value - frac) < Math.abs(b.value - frac) ? f : b, { label: '0', value: 0 });
  const fp = frac < 0.03 ? '' : best.label;
  if (!fp || fp === '0') return sign < 0 ? `-${whole}` : `${whole}`;
  if (whole === 0) return sign < 0 ? `-${fp}` : fp;
  return sign < 0 ? `-${whole} ${fp}` : `${whole} ${fp}`;
};

// ─── Fraction Picker ──────────────────────────────────────────────────────────
const FractionPicker = ({ value, onChange, onClose }) => {
  const dec = fractionToDecimal(value) ?? 0;
  const whole = Math.floor(Math.abs(dec)) * (dec < 0 ? -1 : 1);
  const fracPart = Math.abs(dec) - Math.abs(whole);
  const best = STANDARD_FRACS.reduce((b, f) => Math.abs(f.value - fracPart) < Math.abs(b.value - fracPart) ? f : b, { label: '0', value: 0 });

  const [lw, setLw] = useState(whole);
  const [lf, setLf] = useState(best.value);
  const [manual, setManual] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const manualRef = useRef(null);

  const buildDisplay = (w, fv) => {
    const fl = STANDARD_FRACS.find(f => f.value === fv)?.label || '';
    if (!fl || fl === '0' || fv === 0) return `${w}`;
    if (Math.abs(w) === 0) return fl;
    return `${w} ${fl}`;
  };

  const emit = (w, fv) => onChange(w + fv, buildDisplay(w, fv));

  const handleFrac = (fv) => { setManualMode(false); setLf(fv); emit(lw, fv); };
  const adjustWhole = (d) => { setManualMode(false); const w = lw + d; setLw(w); emit(w, lf); };

  const commitManual = () => {
    if (!manual.trim()) { setManualMode(false); return; }
    const p = fractionToDecimal(manual.trim());
    if (p !== null && !isNaN(p)) {
      const w = Math.floor(Math.abs(p)) * (p < 0 ? -1 : 1);
      const fp = Math.abs(p) - Math.abs(w);
      const nf = STANDARD_FRACS.reduce((b, f) => Math.abs(f.value - fp) < Math.abs(b.value - fp) ? f : b, { label: '0', value: 0 });
      setLw(w); setLf(nf.value);
      onChange(p, manual.trim());
    }
    setManualMode(false); setManual('');
  };

  return (
    <div className="picker-backdrop" onClick={onClose}>
      <div className="picker-popup" onClick={e => e.stopPropagation()}>
        <div className="picker-value-row">
          <button className="picker-icon-btn red" onClick={() => { onChange(null, ''); onClose(); }}>🗑</button>
          <button className="picker-whole-btn" onClick={() => adjustWhole(1)}>﹢</button>
          {manualMode
            ? <input ref={manualRef} className="picker-manual-input" value={manual}
                onChange={e => setManual(e.target.value)} onBlur={commitManual}
                onKeyDown={e => { if (e.key === 'Enter') commitManual(); if (e.key === 'Escape') { setManualMode(false); setManual(''); } }}
                placeholder="e.g. 2 5/8" autoFocus />
            : <div className="picker-display" onClick={() => { setManual(buildDisplay(lw, lf)); setManualMode(true); setTimeout(() => manualRef.current?.select(), 50); }}>
                {buildDisplay(lw, lf) || '0'}<span className="picker-edit-hint">✎</span>
              </div>
          }
          <button className="picker-whole-btn" onClick={() => adjustWhole(-1)}>﹣</button>
          <button className="picker-icon-btn" onClick={onClose}>✕</button>
        </div>
        {!manualMode && <div className="picker-manual-hint" onClick={() => { setManual(buildDisplay(lw, lf)); setManualMode(true); }}>Type manually (e.g. 2 5/8)</div>}
        <div className="picker-fracs">
          {STANDARD_FRACS.map(f => (
            <button key={f.label} className={`picker-frac-btn ${lf === f.value ? 'active' : ''}`} onClick={() => handleFrac(f.value)}>{f.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Session Modal ────────────────────────────────────────────────────────────
const SessionModal = ({ sessions, onSelect, onCreateNew, onClose }) => {
  const [mode, setMode] = useState(sessions.length === 0 ? 'new' : 'pick');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [stage, setStage] = useState('PP');
  const [inspector, setInspector] = useState('');
  const [sampleRef, setSampleRef] = useState('');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="session-modal" onClick={e => e.stopPropagation()}>
        <div className="session-modal-header">
          <h3>📋 Measurement Session</h3>
          <button className="session-close" onClick={onClose}>✕</button>
        </div>
        <div className="session-tabs">
          <button className={`session-tab ${mode === 'pick' ? 'active' : ''}`} onClick={() => setMode('pick')} disabled={sessions.length === 0}>
            Existing ({sessions.length})
          </button>
          <button className={`session-tab ${mode === 'new' ? 'active' : ''}`} onClick={() => setMode('new')}>+ New</button>
        </div>
        {mode === 'pick' ? (
          <div className="session-list">
            {sessions.map(s => (
              <div key={s.public_id} className="session-item" onClick={() => onSelect(s)}>
                <div className="session-item-main">
                  <span className="session-date">{new Date(s.session_date).toLocaleDateString('pt-PT')}</span>
                  <span className={`session-stage stage-${s.stage?.toLowerCase()}`}>{s.stage}</span>
                  {s.overall_result && <span className={`session-result ${s.overall_result === 'PASS' ? 'result-pass' : 'result-fail'}`}>{s.overall_result}</span>}
                </div>
                <div className="session-item-sub">{s.inspector_name || s.inspector_code || '—'}{s.sample_ref && <> · {s.sample_ref}</>}
                  <span className="session-counts">✓ {s.pass_count} · ✗ {s.fail_count}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="session-form">
            {[['Date', <input type="date" value={date} onChange={e => setDate(e.target.value)} />],
              ['Stage', <select value={stage} onChange={e => setStage(e.target.value)}><option value="PP">PP Sample</option><option value="TOP">Top of Production</option><option value="FINAL">Final</option><option value="AQL">AQL</option></select>],
              ['Inspector', <input value={inspector} onChange={e => setInspector(e.target.value)} placeholder="Name or code" />],
              ['Sample Ref', <input value={sampleRef} onChange={e => setSampleRef(e.target.value)} placeholder="Batch / lot reference" />],
            ].map(([label, input]) => (
              <div key={label} className="sf-row"><label>{label}</label>{input}</div>
            ))}
            <button className="sf-create-btn" onClick={() => onCreateNew({ session_date: date, stage, inspector_name: inspector, sample_ref: sampleRef })}>
              Create Session →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const MeasurementEntry = () => {
  const { headerPublicId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toasts, removeToast, toast_success, toast_error, toast_warning } = useToast();

  // Spec data
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);

  // Session
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessionModal, setShowSessionModal] = useState(false);

  // Results: { 'M-1': { lineId: val, ... }, 'M-2': {...}, 'L-1': {...} }
  // Key format: `${size}-${sampleNo}`
  const [results, setResults] = useState({});
  const [sampleCounts, setSampleCounts] = useState({}); // { M: 2, L: 1 }

  // Entry state
  const [activeSize, setActiveSize] = useState(null);
  const [activeSampleNo, setActiveSampleNo] = useState(1); // sample being entered
  const [entryValues, setEntryValues] = useState({}); // { lineId: { actual_value, actual_display } }
  const [activePicker, setActivePicker] = useState(null); // lineId

  const [displayMode, setDisplayMode] = useState('fraction');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showOverview, setShowOverview] = useState(true);

  useEffect(() => { loadSpec(); }, [headerPublicId]);

  const loadSpec = async () => {
    try {
      setLoading(true);
      const [specRes, sessRes] = await Promise.all([
        measurementAPI.getHeaderDetail(headerPublicId),
        measurementAPI.getSessions(headerPublicId)
      ]);
      setSpec(specRes.data.data);
      setSessions(sessRes.data.data);
    } catch (err) {
      toast_error('Failed to load: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadResults = async (sessionPublicId) => {
    try {
      const res = await measurementAPI.getResults(sessionPublicId);
      // Build results map: key = `${size}-${sampleNo}` → { lineId → { actual_value, actual_display, result_status, variance } }
      const map = {};
      res.data.data.forEach(r => {
        const key = `${r.component_code}-${r.sample_no}`;
        if (!map[key]) map[key] = {};
        map[key][r.measurement_line_id] = {
          actual_value: r.actual_value,
          actual_display: r.actual_display,
          result_status: r.result_status,
          variance: r.variance,
          variance_display: r.variance_display,
        };
      });
      setResults(map);
      // Build sampleCounts
      const counts = {};
      res.data.sampleCounts.forEach(sc => { counts[sc.component_code] = parseInt(sc.sample_count); });
      setSampleCounts(counts);
    } catch (err) {
      toast_error('Failed to load results: ' + err.message);
    }
  };

  const handleSelectSession = async (s) => {
    setSession(s);
    setShowSessionModal(false);
    setActiveSize(null);
    setEntryValues({});
    await loadResults(s.public_id);
  };

  const handleCreateSession = async (data) => {
    try {
      const res = await measurementAPI.createSession(headerPublicId, data);
      const s = res.data.data;
      setSessions(prev => [s, ...prev]);
      setSession(s);
      setResults({});
      setSampleCounts({});
      setActiveSize(null);
      setEntryValues({});
      setShowSessionModal(false);
      toast_success('Session created');
    } catch (err) {
      toast_error('Failed to create session: ' + err.message);
    }
  };

  // specsMap: lineId → { componentCode → specRow }
  const specsMap = useMemo(() => {
    if (!spec) return {};
    const map = {};
    spec.specs.forEach(s => {
      if (!map[s.measurement_line_id]) map[s.measurement_line_id] = {};
      map[s.measurement_line_id][s.component_code] = s;
    });
    return map;
  }, [spec]);

  // When a size is selected, start new sample entry
  const selectSize = (size) => {
    const nextSample = (sampleCounts[size] || 0) + 1;
    setActiveSize(size);
    setActiveSampleNo(nextSample);
    setEntryValues({});
    setDirty(false);
    setActivePicker(null);
  };

  const handleEntryChange = (lineId, raw) => {
    setEntryValues(prev => ({ ...prev, [lineId]: { actual_display: raw, actual_value: fractionToDecimal(raw) } }));
    setDirty(true);
  };

  const handleEntryBlur = (lineId, raw) => {
    const parsed = fractionToDecimal(raw.trim());
    if (raw.trim() && parsed !== null && !isNaN(parsed)) {
      setEntryValues(prev => ({ ...prev, [lineId]: { actual_display: raw.trim(), actual_value: parsed } }));
    } else if (!raw.trim()) {
      setEntryValues(prev => { const n = { ...prev }; delete n[lineId]; return n; });
    }
  };

  const handlePickerChange = (lineId, val, display) => {
    if (val === null) {
      setEntryValues(prev => { const n = { ...prev }; delete n[lineId]; return n; });
    } else {
      setEntryValues(prev => ({ ...prev, [lineId]: { actual_display: display, actual_value: val } }));
    }
    setDirty(true);
    setActivePicker(null);
  };

  const handleSaveSample = async () => {
    if (!session) { toast_warning('Select a session first'); return; }
    if (!activeSize) { toast_warning('Select a size first'); return; }
    try {
      setSaving(true);
      const payload = spec.lines.map(line => {
        const lineId = line.measurement_line_id;
        const entry = entryValues[lineId];
        const specRow = specsMap[lineId]?.[activeSize];
        return {
          measurement_line_id: lineId,
          actual_value: entry?.actual_value ?? null,
          actual_display: entry?.actual_display ?? null,
          spec_value: specRow?.measurement_value ?? null,
          spec_display: specRow?.measurement_display ?? null,
          upper_tolerance: line.upper_tolerance,
          lower_tolerance: line.lower_tolerance,
        };
      });

      const res = await measurementAPI.saveResults(session.public_id, {
        results: payload,
        component_code: activeSize,
        sample_no: activeSampleNo,
      });

      const { sampleNo } = res.data;
      // Merge saved results into map
      const key = `${activeSize}-${sampleNo}`;
      const newMap = { ...results, [key]: {} };
      res.data.data.forEach(r => {
        newMap[key][r.measurement_line_id] = {
          actual_value: r.actual_value, actual_display: r.actual_display,
          result_status: r.result_status, variance: r.variance, variance_display: r.variance_display,
        };
      });
      setResults(newMap);
      setSampleCounts(prev => ({ ...prev, [activeSize]: sampleNo }));
      setDirty(false);
      toast_success(`Sample ${activeSize}/${sampleNo} saved`);
    } catch (err) {
      toast_error('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSample = () => {
    // Clear form and increment sample number for same size
    const next = (sampleCounts[activeSize] || 0) + 1;
    setActiveSampleNo(next);
    setEntryValues({});
    setDirty(false);
  };

  // Overview columns: all size+sampleNo combinations that have data, sorted by size order then sample_no
  const overviewColumns = useMemo(() => {
    if (!spec) return [];
    const cols = [];
    spec.sizes.forEach(sz => {
      const count = sampleCounts[sz] || 0;
      for (let n = 1; n <= count; n++) {
        cols.push({ size: sz, sampleNo: n, key: `${sz}-${n}`, label: `${sz}/${n}` });
      }
    });
    return cols;
  }, [spec, sampleCounts]);

  const getEntryDisplay = (lineId, raw) => {
    if (!raw) return '';
    if (displayMode === 'decimal') { const d = fractionToDecimal(raw); return d !== null ? parseFloat(d).toFixed(3) : raw; }
    return raw;
  };

  const getResultDisplay = (lineId, size, sampleNo) => {
    const r = results[`${size}-${sampleNo}`]?.[lineId];
    if (!r || r.actual_value === null) return null;
    if (displayMode === 'variance') return r.variance !== null ? (r.variance >= 0 ? '+' : '') + parseFloat(r.variance).toFixed(3) : null;
    if (displayMode === 'decimal') return parseFloat(r.actual_value).toFixed(3);
    return r.actual_display || decimalToFrac(r.actual_value);
  };

  const getResultStatus = (lineId, size, sampleNo) => {
    return results[`${size}-${sampleNo}`]?.[lineId]?.result_status?.toLowerCase() || 'empty';
  };

  const getSizeStats = (size) => {
    const count = sampleCounts[size] || 0;
    let pass = 0, fail = 0;
    for (let n = 1; n <= count; n++) {
      const sampleData = results[`${size}-${n}`] || {};
      Object.values(sampleData).forEach(r => {
        if (r.result_status === 'PASS') pass++;
        else if (r.result_status === 'FAIL') fail++;
      });
    }
    return { count, pass, fail };
  };

  if (loading) return <div className="settings-page"><div className="me-loading">⏳ Loading…</div></div>;
  if (!spec) return <div className="settings-page"><div className="me-loading">No spec found</div></div>;

  const { header, lines, sizes, defaultSize } = spec;

  return (
    <div className="settings-page me-page">
      <div className="toast-container">
        {toasts.map(t => <Toast key={t.id} {...t} onClose={removeToast} />)}
      </div>

      {/* Top bar */}
      <div className="me-top">
        <button className="btn-back" onClick={() => navigate('/quality/measurements')}>← Back</button>
        <div className="me-title-block">
          <h1 className="me-title">{header.product_code || headerPublicId.slice(0, 8)}</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Display mode */}
          <div className="me-mode-group">
            {['fraction', 'decimal', 'variance'].map(m => (
              <button key={m} className={`me-mode-btn ${displayMode === m ? 'active' : ''}`} onClick={() => setDisplayMode(m)}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
          {/* Session */}
          <button className="me-session-btn" onClick={() => setShowSessionModal(true)}>
            {session ? <>📋 {new Date(session.session_date).toLocaleDateString('pt-PT')} · {session.stage}</> : <>📋 Select Session</>}
          </button>
        </div>
      </div>

      {!session && (
        <div className="me-no-session">Click <strong>Select Session</strong> to start recording measurements</div>
      )}

      {session && (
        <div className="me-body">
          {/* ── LEFT: Size selector ── */}
          <div className="me-size-panel">
            <div className="me-size-panel-title">Select Size</div>
            <div className="me-size-grid">
              {sizes.map(sz => {
                const { count, pass, fail } = getSizeStats(sz);
                const isActive = activeSize === sz;
                const isDefault = sz === defaultSize;
                return (
                  <button
                    key={sz}
                    className={`me-size-btn ${isActive ? 'active' : ''} ${isDefault ? 'is-default' : ''} ${fail > 0 ? 'has-fail' : ''} ${pass > 0 && fail === 0 ? 'all-pass' : ''}`}
                    onClick={() => selectSize(sz)}
                  >
                    <span className="size-btn-code">{sz}{isDefault ? ' ●' : ''}</span>
                    {count > 0 && (
                      <span className="size-btn-meta">
                        {count} sample{count !== 1 ? 's' : ''}
                        {fail > 0 ? <span className="meta-fail"> · {fail}✗</span> : <span className="meta-pass"> · ✓</span>}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── RIGHT: Entry form ── */}
          <div className="me-entry-panel">
            {!activeSize ? (
              <div className="me-entry-placeholder">
                <span>← Select a size to start entering measurements</span>
              </div>
            ) : (
              <>
                <div className="me-entry-header">
                  <div className="me-entry-title">
                    <span className="entry-size-badge">{activeSize}</span>
                    <span className="entry-sample-badge">Sample #{activeSampleNo}</span>
                  </div>
                  <div className="me-entry-actions">
                    <button className="btn-change-size" onClick={() => { setActiveSize(null); setEntryValues({}); setDirty(false); }}>
                      ↩ Change Size
                    </button>
                    <button
                      className="btn-save-sample"
                      onClick={handleSaveSample}
                      disabled={saving || !dirty}
                    >
                      {saving ? '⏳ Saving…' : '💾 Save Sample'}
                    </button>
                  </div>
                </div>

                {/* Measurement lines */}
                <div className="me-lines-list">
                  {lines.map(line => {
                    const lineId = line.measurement_line_id;
                    const specRow = specsMap[lineId]?.[activeSize];
                    const entry = entryValues[lineId];
                    const isPickerOpen = activePicker === lineId;

                    const specDisplay = specRow
                      ? (displayMode === 'decimal'
                          ? parseFloat(specRow.measurement_value).toFixed(3)
                          : specRow.measurement_display || decimalToFrac(specRow.measurement_value))
                      : null;

                    const tolDisplay = line.upper_tolerance > 0
                      ? `± ${displayMode === 'fraction' ? decimalToFrac(parseFloat(line.upper_tolerance)) : parseFloat(line.upper_tolerance).toFixed(2)}`
                      : null;

                    return (
                      <div key={lineId} className={`me-line-row ${!specRow ? 'line-no-spec' : ''}`}>
                        <div className="line-code">{line.code}</div>
                        <div className="line-desc">
                          {line.description}
                          {tolDisplay && <span className="line-tol">{tolDisplay}</span>}
                        </div>
                        <div className="line-spec">{specDisplay ?? <span className="cell-na">—</span>}</div>
                        <div className="line-input-wrap">
                          <input
                            className={`line-input ${entry ? (
                              (() => {
                                if (!specRow) return '';
                                const v = entry.actual_value;
                                const sv = specRow.measurement_value;
                                if (v === null || sv === null) return '';
                                const diff = parseFloat(v) - parseFloat(sv);
                                const up = parseFloat(line.upper_tolerance ?? 999);
                                const lo = parseFloat(line.lower_tolerance ?? 999);
                                return (diff > up || diff < -lo) ? 'input-fail' : 'input-pass';
                              })()
                            ) : ''}`}
                            value={entry?.actual_display || ''}
                            disabled={!specRow}
                            placeholder={specRow ? '·' : '—'}
                            onChange={e => handleEntryChange(lineId, e.target.value)}
                            onBlur={e => handleEntryBlur(lineId, e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                          />
                          {specRow && (
                            <button
                              className={`line-picker-btn ${isPickerOpen ? 'active' : ''}`}
                              onClick={() => setActivePicker(isPickerOpen ? null : lineId)}
                            >⌨</button>
                          )}
                        </div>
                        {isPickerOpen && (
                          <FractionPicker
                            value={entry?.actual_display || ''}
                            onChange={(val, display) => handlePickerChange(lineId, val, display)}
                            onClose={() => setActivePicker(null)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Bottom actions */}
                <div className="me-entry-footer">
                  <button
                    className="btn-save-sample btn-save-sample-full"
                    onClick={handleSaveSample}
                    disabled={saving || !dirty}
                  >
                    {saving ? '⏳ Saving…' : `💾 Save ${activeSize} Sample #${activeSampleNo}`}
                  </button>
                  <button
                    className="btn-add-sample"
                    onClick={handleAddSample}
                    disabled={dirty}
                    title={dirty ? 'Save current sample first' : `Start Sample #${(sampleCounts[activeSize] || 0) + 1}`}
                  >
                    + Add Sample #{(sampleCounts[activeSize] || 0) + 1}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Overview matrix ── */}
      {session && overviewColumns.length > 0 && (
        <div className="me-overview">
          <div className="me-overview-header">
            <span className="me-overview-title">📊 Overview</span>
            <button className="me-overview-toggle" onClick={() => setShowOverview(v => !v)}>
              {showOverview ? '▲ Collapse' : '▼ Expand'}
            </button>
          </div>
          {showOverview && (
            <div className="me-grid-wrap">
              <table className="me-table">
                <thead>
                  <tr>
                    <th className="me-th-line">Code</th>
                    <th className="me-th-desc">Description</th>
                    {overviewColumns.map(col => (
                      <th key={col.key}
                        className={`me-th-size ${col.size === activeSize ? 'me-th-active-size' : ''} ${col.size === defaultSize ? 'me-th-default' : ''}`}
                        onClick={() => selectSize(col.size)}
                        title={`Click to enter ${col.size} sample ${col.sampleNo}`}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.map(line => {
                    const lineId = line.measurement_line_id;
                    return (
                      <tr key={lineId} className="me-tr">
                        <td className="me-td-code">{line.code}</td>
                        <td className="me-td-desc">{line.description}</td>
                        {overviewColumns.map(col => {
                          const val = getResultDisplay(lineId, col.size, col.sampleNo);
                          const status = getResultStatus(lineId, col.size, col.sampleNo);
                          const specRow = specsMap[lineId]?.[col.size];
                          return (
                            <td key={col.key}
                              className={`me-td-cell ${specRow ? `status-${status}` : 'cell-no-spec'} ${col.size === activeSize ? 'cell-active-col' : ''}`}
                              onClick={() => selectSize(col.size)}
                            >
                              <div className="cell-actual">{val ?? <span className="cell-placeholder">·</span>}</div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showSessionModal && (
        <SessionModal sessions={sessions} onSelect={handleSelectSession} onCreateNew={handleCreateSession} onClose={() => setShowSessionModal(false)} />
      )}
    </div>
  );
};

export default MeasurementEntry;